import {
  ACADEMIC_METRIC_FIELDS,
  ARRAY_FIELDS,
  JSON_PARSEABLE_FIELDS,
} from '@backrs_ui/grow-ui/src/pages/Profile/constants/profile.constants';
import {
  CreateFieldConfigProps,
  ProfileFieldConfig,
  ProfileSectionConfig,
  DocumentType,
  FileUploadConfig,
  NumberValidation,
  Validation,
  AcademicMetricValue,
  ApTest,
  EntranceExam,
  ProfileValue,
  AcademicMajorValue,
} from '@backrs_ui/grow-ui/src/pages/Profile/types/profile.types';
import {
  ProfileValueOption,
  ProfileValueOptionResponse,
  ProfileFields,
  UserProfileResponse,
  AutocompleteOption,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import type { useUpdateUserProfileMutation } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { FieldValues } from 'react-hook-form';

const formatLabel = (key: string) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

export const createFieldConfig = ({
  key,
  type,
  chipType,
  inlineChip,
  width,
  maxSelectedOptions,
  showSearch,
  textLeadingAdornment,
  validation,
  placeholder,
  disabledBy,
  customOptions,
  setsValue,
  enabledBy,
}: CreateFieldConfigProps) => ({
  key,
  type,
  chipType,
  inlineChip,
  label: formatLabel(key),
  maxSelectedOptions,
  width,
  showSearch,
  textLeadingAdornment,
  validation,
  placeholder,
  disabledBy,
  customOptions,
  setsValue,
  enabledBy,
});

type DetermineDefaultValueProps = Readonly<{
  config: {
    key: ProfileFieldConfig['key'];
    type: ProfileFieldConfig['type'];
    chipType?: ProfileFieldConfig['chipType'];
    width?: ProfileFieldConfig['width'];
    createDynamicInput?: ProfileFieldConfig['createDynamicInput'];
    dynamicInputConfig?: ProfileFieldConfig['dynamicInputConfig'];
    maxSelectedOptions?: ProfileFieldConfig['maxSelectedOptions'];
    setsValue?: ProfileFieldConfig['setsValue'];
  };
  filteredOptions: (ProfileValueOption | string)[];
}>;

export const determineDefaultValue = ({ config, filteredOptions }: DetermineDefaultValueProps) => {
  if (config.type.variant === 'DOCUMENT') {
    return '';
  }

  if (config.type.variant === 'API_AUTOCOMPLETE') {
    if (config.maxSelectedOptions === 1) {
      return filteredOptions[0];
    }

    return filteredOptions as ProfileValueOption[];
  }

  if (
    (config.type.variant === 'SINGLE_SELECT' || config.type.variant === 'TEXT') &&
    !config.chipType
  ) {
    return filteredOptions[0];
  }
  if (
    config.type.variant === 'AUTOCOMPLETE' ||
    config.type.variant === 'CHECKBOX_GROUP' ||
    config.type.variant === 'MULTIPLE_SELECT'
  ) {
    return filteredOptions as ProfileValueOption[] | string[];
  }

  if (config.type.variant === 'TOGGLE') {
    return filteredOptions[0] === 'true' ? '1' : '0';
  }

  return undefined;
};

const MAX_LIMIT_10MB = 10 * 1024 * 1024;
const MAX_LIMIT_512MB = 512 * 1024 * 1024;

export const DOCUMENT_CONFIGS: Record<DocumentType, FileUploadConfig> = {
  resume: {
    type: 'resume',
    title: 'Resume',
    description: 'Upload your resume here.',
    acceptedFileTypes: ['.pdf', '.docx', '.jpg', '.png'],
    maxFileSize: MAX_LIMIT_10MB,
    categoryId: 'story',
    questionId: 'document',
    optionId: 'resume',
    visibility: 'panel',
    key: 'documentsResumes',
    mediaType: 'document',
  },
  transcript: {
    type: 'transcript',
    title: 'Transcript',
    description: 'Upload your transcript here.',
    acceptedFileTypes: ['.pdf', '.docx', '.jpg', '.png'],
    maxFileSize: MAX_LIMIT_10MB,
    categoryId: 'story',
    questionId: 'document',
    optionId: 'transcript',
    visibility: 'panel',
    key: 'documentsTranscripts',
    mediaType: 'document',
  },
  personalAssessment: {
    type: 'personalAssessment',
    title: 'Personal Assessment',
    description: 'Upload your aptitude, personality, strengths, and/or career assessment here.',
    acceptedFileTypes: ['.pdf', '.docx', '.jpg', '.png'],
    maxFileSize: MAX_LIMIT_10MB,
    categoryId: 'story',
    questionId: 'document',
    optionId: 'personal_assessment',
    visibility: 'public',
    key: 'documentsPersonalAssessment',
    mediaType: 'document',
  },
  other: {
    type: 'other',
    title: 'Other',
    description: 'Upload your other documents here.',
    acceptedFileTypes: ['.pdf', '.docx', '.jpg', '.png'],
    maxFileSize: MAX_LIMIT_10MB,
    key: 'documentsOther',
    mediaType: 'document',
    categoryId: 'story',
    questionId: 'document',
    visibility: 'public',
    optionId: 'other',
  },
  purpose_video: {
    type: 'purpose_video',
    title: 'Purpose Video',
    description: 'Upload your purpose video here.',
    acceptedFileTypes: ['.mp4', '.mov'],
    maxFileSize: MAX_LIMIT_512MB,
    key: 'documentsPurposeVideo',
    categoryId: 'video',
    questionId: 'video',
    visibility: 'public',
    mediaType: 'video',
    overrideModal: true,
  },
};

export const determineIfInputDisabled = (
  field: ProfileFieldConfig,
  userProfile?: UserProfileResponse,
  options?: ProfileValueOptionResponse,
) => {
  if (!userProfile) return false;

  if (field?.enabledBy) {
    const enabledByValues = field?.enabledBy?.values(options);
    const actualFieldValue =
      userProfile?.profile?.[field?.enabledBy?.field as keyof typeof userProfile.profile];

    // Handle dynamic values (functions) vs static values (arrays)

    if (enabledByValues?.includes(actualFieldValue?.toString() ?? null)) {
      return false;
    }

    return true;
  }

  const disabledByValues = field?.disabledBy?.values(options);
  const actualFieldValue =
    userProfile?.profile?.[field?.disabledBy?.field as keyof typeof userProfile.profile];

  // if the actual field value is an object, check if it has an id to use
  const doesActualFieldValueHaveID =
    actualFieldValue && typeof actualFieldValue === 'object' && 'id' in actualFieldValue;

  if (doesActualFieldValueHaveID) {
    return disabledByValues?.includes(actualFieldValue?.id?.toString() ?? null);
  }

  // Handle dynamic values (functions) vs static values (arrays)

  const isDisabled = disabledByValues?.includes(actualFieldValue?.toString() ?? null);

  return isDisabled;
};

export const determineIfSectionHidden = (
  section: ProfileSectionConfig,
  userProfile?: UserProfileResponse,
  options?: Record<string, ProfileValueOptionResponse[ProfileFields]>,
) => {
  if (!userProfile || !section.hiddenBy) return false;

  const hiddenByValues = section.hiddenBy.values(options);
  const actualFieldValue =
    userProfile.profile[section.hiddenBy.field as keyof typeof userProfile.profile];

  // if the actual field value is an object, check if it has an id to use
  const doesActualFieldValueHaveID =
    actualFieldValue && typeof actualFieldValue === 'object' && 'id' in actualFieldValue;

  if (doesActualFieldValueHaveID) {
    return hiddenByValues?.includes(actualFieldValue?.id?.toString() ?? null);
  }

  const isHidden = hiddenByValues?.includes(actualFieldValue?.toString() ?? null);

  return isHidden;
};

// Helper function to format URLs
export const formatUrl = (value: string): string => {
  if (!value.startsWith('http://') && !value.startsWith('https://')) {
    return `http://${value}`;
  }

  return value;
};

// Helper function to get default value
export const getDefaultValue = (
  defaultValue: string | { id: number } | (string | { id: number })[] | undefined,
): string | string[] => {
  if (!defaultValue) return '';

  if (Array.isArray(defaultValue)) {
    return defaultValue.map((opt) => (typeof opt === 'string' ? opt : opt.id.toString()));
  }

  if (typeof defaultValue === 'string') {
    return defaultValue;
  }

  return defaultValue.id.toString();
};

// Helper function to validate numbers
export const validateNumber = (value: string, validation: NumberValidation): boolean => {
  const num = Number(value);

  if (isNaN(num)) return false;

  if (validation.min !== undefined && num < validation.min) return false;
  if (validation.max !== undefined && num > validation.max) return false;

  if (validation.format === 'whole' && !Number.isInteger(num)) return false;
  if (validation.format === 'decimal') {
    const decimals = value.split('.')[1]?.length ?? 0;

    if (decimals > (validation.decimals ?? 2)) return false;
  }

  return true;
};

// Helper function to validate values
export const validateValue = (value: string, validation?: Validation): boolean => {
  if (!validation) return true;
  if (value === '') return true;

  if (validation.type === 'number') {
    return validateNumber(value, validation);
  }

  return true;
};

// Helper function to format options
export const formatOptions = (
  options: ProfileValueOptionResponse[ProfileFields],
  fieldKey: string,
) => {
  if (!options) return [];

  if (Array.isArray(options)) {
    return options.map((opt) => {
      if (!opt) return { label: '', value: '' };
      if ('club' in opt) return { label: opt.club, value: opt.id.toString() };
      if ('language' in opt) return { label: opt.language, value: opt.id.toString() };
      if ('school' in opt) return { label: opt.school, value: opt.id.toString() };
      if ('media_url' in opt) return { label: opt.title, value: opt.id.toString(), opt };

      return { label: opt.name, value: opt.id.toString() };
    });
  }

  if (typeof options === 'object' && fieldKey in options) {
    const value = options[fieldKey as keyof typeof options];

    if (!value) return [];

    const isNameAndIdPresent =
      typeof value === 'object' && value !== null && ('name' in value || 'id' in value);

    if (isNameAndIdPresent) {
      return [
        {
          label: (value as { name?: string }).name || '',
          value: (value as { id?: number }).id?.toString() || '',
        },
      ];
    }
  }

  return [];
};

// Helper function to handle academic metric changes
export const handleAcademicMetricChange = (field: ProfileFieldConfig, value: string): void => {
  const metricValue = value ? Number(value) : undefined;
  const metricObject: AcademicMetricValue = { [field.key]: metricValue };

  field.onChange?.([metricObject]);
};

// Helper function to handle URL field changes
export const handleUrlFieldChange = (field: ProfileFieldConfig, value: string): void => {
  field.onChange?.(value ? [formatUrl(value)] : []);
};

// Helper function to handle text field changes
export const handleTextFieldChange = (field: ProfileFieldConfig, value: string): void => {
  if (validateValue(value, field.validation)) {
    const isAcademicMetric = ['gpa', 'major_gpa', 'sat_score', 'act_score'].includes(field.key);
    const isUrlField = ['linkedInData', 'calendlyData'].includes(field.key);

    if (isAcademicMetric) {
      handleAcademicMetricChange(field, value);
    } else if (isUrlField) {
      handleUrlFieldChange(field, value);
    } else {
      field.onChange?.(value ? [value] : []);
    }
  }
};

/**
 * Handles field value changes, with special logic for toggle fields that set other field values.
 *
 * For toggle fields with setsValue configuration:
 * - When toggled ON: Sets the target field to the configured value
 * - When toggled OFF: Sets the target field to null
 * - Triggers UI refetch to synchronize with backend changes
 *
 * For regular fields: Updates the field value directly
 */
export const handleFieldChange = ({
  config,
  values,
  fieldKey,
  handleUpdatingAnswer,
  refetchUserProfile,
}: {
  config: CreateFieldConfigProps;
  values: (
    | number
    | string
    | AcademicMetricValue
    | ApTest
    | EntranceExam
    | AcademicMajorValue
    | AutocompleteOption
  )[];
  fieldKey: string;
  handleUpdatingAnswer: (
    key: string,
    value: (
      | number
      | string
      | AcademicMetricValue
      | ApTest
      | EntranceExam
      | AcademicMajorValue
      | AutocompleteOption
      | null
    )[],
    displayProperty?: string,
    fieldConfig?: CreateFieldConfigProps,
  ) => void;
  refetchUserProfile: () => void;
}) => {
  // Extract displayProperty from API_AUTOCOMPLETE config
  const displayProperty =
    config.type.variant === 'API_AUTOCOMPLETE' ? config.type.displayProperty : undefined;

  // Check if this field should set another field's value
  if (config.setsValue) {
    const { field: targetField, value: targetValue } = config.setsValue;

    // For toggle fields, handle the on/off logic
    if (config.type.variant === 'TOGGLE') {
      const isToggleOn = values[0] === 1;
      const valueToSet = isToggleOn ? targetValue : null;

      handleUpdatingAnswer(targetField, [valueToSet], displayProperty, config);

      // Refetch to update the UI with new backend state
      setTimeout(() => refetchUserProfile(), 500);

      return;
    }

    // Additional refetch for UI synchronization
    setTimeout(() => refetchUserProfile(), 500);

    return;
  }

  // Standard field update
  handleUpdatingAnswer(fieldKey, values, displayProperty, config);
};

type HandleApTestChangeProps = Readonly<{
  field: ProfileFieldConfig;
  values: (number | string)[];
  getValues: () => FieldValues;
  /** we need to also update RHF state when we clear the value */
  setRHFValue: (name: string, value: unknown) => void;
  /** from the dynamic input, we need a way to clear with the X */
  clearZeroScores: boolean;
}>;

export const handleApTestChange = ({
  field,
  values,
  getValues,
  setRHFValue,
  clearZeroScores,
}: HandleApTestChangeProps): void => {
  const selectedIds = values.map((v) => (typeof v === 'string' ? Number(v) : v));

  const allValues = getValues();

  // handle updating RHF state when we clear the value
  Object.keys(allValues).forEach((key) => {
    if (key.startsWith('academicApTests_')) {
      const testId = Number(key.replace('academicApTests_', ''));

      if (!selectedIds.includes(testId)) {
        setRHFValue(key, undefined);
      }
    }
  });

  // now handle redux / backend updates
  const apTestValues = selectedIds
    .map((id) => {
      const rawScore = allValues[`academicApTests_${id}`];

      if ((rawScore === null || rawScore === '' || rawScore === '0') && clearZeroScores) {
        return null;
      }

      const minScore = field.validation?.type === 'number' ? field.validation.min : 1;

      const score = rawScore && rawScore !== '' ? Number(rawScore) : minScore;

      return {
        ap_test_id: id,
        score,
      };
    })
    .filter((test): test is { ap_test_id: number; score: number } => test !== null);

  field.onChange(apTestValues);
};

const buildAcademicMajorValue = (
  id: number,
  rawScore: string | number | undefined,
  validation: Validation | undefined,
): { academic_majors_id: number; major_gpa: number | undefined } => {
  const minScore = validation?.type === 'number' ? undefined : Number('4.0');
  const major_gpa = rawScore !== undefined && rawScore !== '' ? Number(rawScore) : minScore;

  return {
    academic_majors_id: id,
    major_gpa,
  };
};

export const handleAcademicMajorChange = (
  field: ProfileFieldConfig,
  values: (number | string | (ProfileValueOption & { major_gpa: number }))[],
  getValues: () => FieldValues,
): void => {
  const academicMajorValues = values.map((value) => {
    if (typeof value === 'object' && 'major_gpa' in value) {
      // Value is already an object with GPA
      return {
        academic_majors_id: value.id,
        major_gpa: value.major_gpa,
      };
    } else {
      // Value is just an ID, need to get GPA from form or default
      const id = typeof value === 'string' ? Number(value) : value;
      const allValues = getValues();
      const rawScore = allValues[`major_gpa_${id}`];

      return buildAcademicMajorValue(id, rawScore, field.validation);
    }
  });

  // Create a new field object with the correct key for the onChange handler
  const academicMajorsField = {
    ...field,
    key: 'academicMajors' as ProfileFields,
  };

  academicMajorsField.onChange(academicMajorValues);
};

export const handleAcademicMajorSelection = (
  values: (string | number)[],
  field: ProfileFieldConfig,
  getValues: () => FieldValues,
) => {
  const selectedIds = values.map((v) => (typeof v === 'string' ? Number(v) : v));

  const existingMajors = Array.isArray(field.defaultValue)
    ? field.defaultValue.filter(
        (opt): opt is ProfileValueOption & { major_gpa: number } =>
          typeof opt !== 'string' && 'major_gpa' in opt,
      )
    : [];

  const valuesWithGpa = selectedIds.map((id) => {
    const existingMajor = existingMajors.find((major) => major.id === id);

    return existingMajor || { id, major_gpa: 0.0, name: '' };
  });

  handleAcademicMajorChange(field, valuesWithGpa, getValues);
};

export const handleEntranceExamChange = (
  field: ProfileFieldConfig,
  values: (number | string)[],
  getValues: () => FieldValues,
  profileValues?: ProfileValueOptionResponse,
): void => {
  const selectedIds = values.map((v) => (typeof v === 'string' ? Number(v) : v));

  const allValues = getValues();

  const entranceExamValues = selectedIds.map((id) => {
    const mappedOption = profileValues?.academicEntranceExams?.find((option) => option.id === id);

    const dynamicConfig = mappedOption
      ? field.dynamicInputConfig?.getDynamicConfig?.(mappedOption)
      : null;

    const rawScore = allValues[`academicEntranceExams_${id}`];

    const minScore =
      dynamicConfig?.validation?.type === 'number' ? dynamicConfig.validation.min : 1;

    const score = rawScore !== undefined && rawScore !== '' ? Number(rawScore) : minScore;

    return {
      entrance_exam_id: id,
      score: score,
    };
  });

  field.onChange(entranceExamValues);
};

// Type guards
export const isAcademicMetricField = (
  key: string,
): key is (typeof ACADEMIC_METRIC_FIELDS)[number] => {
  return ACADEMIC_METRIC_FIELDS.includes(key as (typeof ACADEMIC_METRIC_FIELDS)[number]);
};

export const isArrayField = (key: string): key is (typeof ARRAY_FIELDS)[number] => {
  return ARRAY_FIELDS.includes(key as (typeof ARRAY_FIELDS)[number]);
};

export const isJsonParseableField = (
  key: string,
): key is (typeof JSON_PARSEABLE_FIELDS)[number] => {
  return JSON_PARSEABLE_FIELDS.includes(key as (typeof JSON_PARSEABLE_FIELDS)[number]);
};

export const isObjectValue = (value: ProfileValue): value is AcademicMetricValue => {
  return typeof value === 'object' && value !== null;
};

// Utility functions
export const parseJsonValue = (value: ProfileValue): ProfileValue => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
};

export const parseJsonValues = (values: ProfileValue[]): ProfileValue[] => {
  return values.map(parseJsonValue);
};

// Field update handlers
export const handleAcademicMetricUpdate = async (
  updateProfile: ReturnType<typeof useUpdateUserProfileMutation>[0],
  value: ProfileValue[],
): Promise<void> => {
  if (value.length > 0 && isObjectValue(value[0])) {
    await updateProfile({
      field: 'academicMetrics',
      value: value[0] as AcademicMetricValue,
    }).unwrap();
  }
};

export const handleMajorGpaUpdate = async (
  updateProfile: ReturnType<typeof useUpdateUserProfileMutation>[0],
  value: ProfileValue[],
): Promise<void> => {
  const parsedValue = parseJsonValues(value);

  await updateProfile({
    field: 'academicMajors' as ProfileFields,
    value: parsedValue as (number | string)[],
  }).unwrap();
};

const handleJsonParseableFieldUpdate = async (
  updateProfile: ReturnType<typeof useUpdateUserProfileMutation>[0],
  key: string,
  value: ProfileValue[],
): Promise<void> => {
  const parsedValue = parseJsonValues(value);

  await updateProfile({
    field: key as ProfileFields,
    value: parsedValue as (number | string)[],
  }).unwrap();
};

// Helper function to process individual autocomplete values
type ProcessedAutocompleteValue = string | number | Record<string, string | number | null>;
const processApiAutocompleteValue = (
  item: ProfileValue,
  displayProperty?: string,
): ProcessedAutocompleteValue => {
  if (typeof item === 'string' || typeof item === 'number') {
    return item;
  }

  if (item === null) {
    return String(item);
  }

  // Handle AutocompleteOption objects
  if (typeof item === 'object' && 'label' in item && 'value' in item) {
    const autocompleteItem = item as AutocompleteOption;
    const { label, value: val, ...additionalProps } = autocompleteItem;

    if (Object.keys(additionalProps).length > 0) {
      return additionalProps; // Send all additional properties
    } else {
      return val || label || String(item);
    }
  }

  // Handle ProfileValueOption objects
  if (typeof item === 'object' && 'name' in item) {
    return (item as ProfileValueOption).name;
  }

  // Handle other object types (AcademicMetricValue, ApTest, EntranceExam)
  if (typeof item === 'object') {
    const stringVal =
      displayProperty && displayProperty in item
        ? String((item as Record<string, string | number>)[displayProperty])
        : String(item);

    return stringVal;
  }

  return String(item);
};

// Generic handler for API autocomplete fields that preserves all properties
export const handleApiAutocompleteFieldUpdate = async (
  updateProfile: ReturnType<typeof useUpdateUserProfileMutation>[0],
  key: string,
  value: ProfileValue[],
  displayProperty?: string,
): Promise<void> => {
  let patchValue:
    | (string | Record<string, string | number | null>)[]
    | string
    | number
    | Record<string, string | number | null>;

  // Handle empty values
  if (!value || value.length === 0) {
    patchValue = [];
  }
  // Handle array values (multi-select fields like academicsAlmaMaters)
  else if (isArrayField(key) && Array.isArray(value)) {
    patchValue = value.map((item) => processApiAutocompleteValue(item, displayProperty)) as (
      | string
      | Record<string, string | number | null>
    )[];
  }
  // Handle single values
  else {
    patchValue = processApiAutocompleteValue(value[0], displayProperty);
  }

  await updateProfile({
    field: key as ProfileFields,
    value: patchValue,
  }).unwrap();
};

export const handleStandardFieldUpdate = async (
  updateProfile: ReturnType<typeof useUpdateUserProfileMutation>[0],
  key: string,
  value: ProfileValue[],
): Promise<void> => {
  const fieldValue = isArrayField(key) ? value : value[0];

  await updateProfile({
    field: key as ProfileFields,
    value: fieldValue as number | string | (number | string)[] | null,
  }).unwrap();
};

export const handleUpdatingAnswer = async (
  updateProfile: ReturnType<typeof useUpdateUserProfileMutation>[0],
  key: string,
  value: ProfileValue[],
  displayProperty?: string,
  fieldConfig?: CreateFieldConfigProps,
): Promise<void> => {
  try {
    // Handle enhanced autocomplete fields (any field with metadata)
    if (
      fieldConfig?.type?.variant === 'API_AUTOCOMPLETE' &&
      fieldConfig.type.expectInstitutionalOptions
    ) {
      await handleApiAutocompleteFieldUpdate(updateProfile, key, value, displayProperty);

      return;
    }

    // Handle academic metric fields
    if (isAcademicMetricField(key) && value.length > 0 && isObjectValue(value[0])) {
      await handleAcademicMetricUpdate(updateProfile, value);

      return;
    }

    // Handle major_gpa field specifically
    if (key === 'major_gpa') {
      await handleMajorGpaUpdate(updateProfile, value);

      return;
    }

    // Handle JSON parseable fields
    if (isJsonParseableField(key)) {
      await handleJsonParseableFieldUpdate(updateProfile, key, value);

      return;
    }

    // Handle standard fields
    await handleStandardFieldUpdate(updateProfile, key, value);
  } catch (error) {
    console.error('Failed to update profile:', error);
  }
};
